import React, { useState } from 'react';
import { X, ShoppingBag, Minus, Plus, AlertCircle, CheckCircle, Wallet, Info } from 'lucide-react';
import { toast } from 'react-hot-toast';
import marketplaceAPI from '../../services/marketplace.api';
import useAuthStore from '../../store/authStore';

const OrderModal = ({ item, type, business, onClose, onSuccess }) => {
  const { user } = useAuthStore();
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState('');

  const isProduct = type === 'product';
  const itemName = isProduct ? item.name : item.title;
  const itemPrice = isProduct ? item.price : item.basePrice;
  const maxQuantity = isProduct ? (item.quantity || 99) : 1;
  const totalAmount = itemPrice * quantity;

  const handleQuantityChange = (delta) => {
    const newQuantity = quantity + delta;
    if (newQuantity >= 1 && newQuantity <= maxQuantity) {
      setQuantity(newQuantity);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please login to place an order');
      return;
    }

    setIsLoading(true);
    try {
      const orderData = {
        businessId: business.id,
        ...(isProduct ? { productId: item.id } : { serviceId: item.id }),
        quantity,
        price: itemPrice,
        metadata: {
          specialInstructions: specialInstructions.trim() || undefined,
          itemName,
          businessName: business.name
        }
      };

      const response = await marketplaceAPI.createOrder(orderData);
      
      toast.success('Order placed successfully!');
      onSuccess?.(response.data);
      onClose();
    } catch (error) {
      console.error('Failed to place order:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to place order');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4" style={{ paddingBottom: 'calc(1rem + 65px)' }}>
      <div className="bg-[#E8ECF5] rounded-2xl shadow-neomorph w-full max-w-md flex flex-col border border-primary-100/30 mx-3 sm:mx-0 max-h-[85vh] sm:max-h-[90vh]">
        
        {/* Header - Fixed */}
        <div className="p-4 sm:p-6 border-b border-primary-100/50 flex-shrink-0 bg-[#E8ECF5] rounded-t-2xl">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="flex items-center min-w-0 flex-1">
              <div className="w-1 h-6 sm:h-8 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2 sm:mr-3" />
              <div className="min-w-0">
                <h3 className="text-base sm:text-xl font-bold text-gray-800">Place Order</h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Complete your purchase</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-gray-500 flex-shrink-0 ml-2"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>

          {/* Item Summary */}
          <div className="flex items-start space-x-2 sm:space-x-4 p-3 sm:p-4 bg-white rounded-xl shadow-neomorph-inset border border-primary-100/30">
            {item.images?.[0] ? (
              <img
                src={item.images[0]}
                alt={itemName}
                className="h-16 w-16 sm:h-20 sm:w-20 rounded-xl object-cover flex-shrink-0 border-2 border-primary-200"
              />
            ) : (
              <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center flex-shrink-0">
                <ShoppingBag className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-800 text-sm sm:text-base truncate">{itemName}</h4>
              <p className="text-[11px] sm:text-xs text-gray-500 truncate mt-0.5">{business.name}</p>
              <div className="mt-1.5 sm:mt-2 flex flex-wrap items-baseline gap-1 sm:gap-2">
                <span className="text-base sm:text-xl font-bold text-gray-800">
                  ₦{itemPrice?.toLocaleString()}
                </span>
                {isProduct && item.compareAtPrice && (
                  <span className="text-xs sm:text-sm text-gray-500 line-through">
                    ₦{item.compareAtPrice.toLocaleString()}
                  </span>
                )}
              </div>
              {isProduct && maxQuantity < 99 && (
                <p className="text-[10px] sm:text-xs text-green-600 mt-1 flex items-center">
                  <CheckCircle className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                  {maxQuantity} available
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-5">
          {/* Quantity Selector - Only for products */}
          {isProduct && (
            <div className="bg-white rounded-xl shadow-neomorph p-3 sm:p-4 border border-primary-100/30">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3">
                Quantity
              </label>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1.5 sm:space-x-2">
                  <button
                    type="button"
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= 1}
                    className="p-1.5 sm:p-2 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all disabled:opacity-50 border border-primary-100/30"
                  >
                    <Minus className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-600" />
                  </button>
                  <span className="w-12 sm:w-16 text-center font-semibold text-gray-800 bg-gray-50 py-1.5 sm:py-2 rounded-lg shadow-neomorph-inset text-sm">
                    {quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleQuantityChange(1)}
                    disabled={quantity >= maxQuantity}
                    className="p-1.5 sm:p-2 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all disabled:opacity-50 border border-primary-100/30"
                  >
                    <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-600" />
                  </button>
                </div>
                <span className="text-[11px] sm:text-sm text-gray-500 bg-gray-50 px-2 sm:px-3 py-1 sm:py-2 rounded-lg">
                  Max: {maxQuantity}
                </span>
              </div>
            </div>
          )}

          {/* Special Instructions */}
          <div className="bg-white rounded-xl shadow-neomorph p-3 sm:p-4 border border-primary-100/30">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              Special Instructions (Optional)
            </label>
            <textarea
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              placeholder="Add any special requests or notes for the seller..."
              rows={3}
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 resize-none text-sm"
              maxLength={500}
            />
            <div className="mt-1.5 sm:mt-2 text-right text-[10px] sm:text-xs text-gray-500">
              {specialInstructions.length}/500
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-xl shadow-neomorph p-3 sm:p-4 border border-primary-200">
            <h4 className="text-xs sm:text-sm font-semibold text-gray-800 mb-2 sm:mb-3 flex items-center">
              <div className="w-1 h-3 sm:h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5 sm:mr-2" />
              Order Summary
            </h4>
            <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
              <div className="flex justify-between items-center p-2 sm:p-2.5 bg-white rounded-lg">
                <span className="text-gray-600 text-[11px] sm:text-sm">
                  {isProduct ? 'Product' : 'Service'} ({quantity}x)
                </span>
                <span className="font-medium text-gray-800 text-[11px] sm:text-sm">₦{itemPrice?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center p-2 sm:p-2.5 bg-white rounded-lg">
                <span className="text-gray-600 text-[11px] sm:text-sm">Subtotal</span>
                <span className="font-medium text-gray-800 text-[11px] sm:text-sm">₦{totalAmount.toLocaleString()}</span>
              </div>
              {isProduct && item.compareAtPrice && item.compareAtPrice > itemPrice && (
                <div className="flex justify-between items-center p-2 sm:p-2.5 bg-green-50 rounded-lg border border-green-200">
                  <span className="text-green-600 text-[11px] sm:text-sm">You save</span>
                  <span className="font-medium text-green-700 text-[11px] sm:text-sm">
                    ₦{((item.compareAtPrice || itemPrice) - itemPrice).toLocaleString()}
                  </span>
                </div>
              )}
              <div className="border-t border-primary-200 pt-2 sm:pt-3 mt-2 sm:mt-3">
                <div className="flex justify-between items-center font-bold text-gray-800">
                  <span className="text-xs sm:text-sm">Total</span>
                  <span className="text-base sm:text-xl text-primary-600">₦{totalAmount.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer with Submit Button - Fixed at bottom */}
        <div className="p-4 sm:p-6 border-t border-primary-100/50 bg-[#E8ECF5] rounded-b-2xl flex-shrink-0">
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm order-2 sm:order-1 disabled:opacity-50"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading || (isProduct && quantity > maxQuantity)}
              className="px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all flex items-center justify-center text-sm order-1 sm:order-2 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 border-b-2 border-white mr-1.5 sm:mr-2"></div>
                  <span className="text-xs sm:text-sm">Processing...</span>
                </>
              ) : (
                <>
                  <ShoppingBag className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                  <span className="text-xs sm:text-sm">Place Order (₦{totalAmount.toLocaleString()})</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderModal;